import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrder } from "../queries/ordersQueries";
import { formatPrice } from "../utils/currency";
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Anchor,
  Center,
  Loader,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleCheck,
} from "@tabler/icons-react";

const statusColors: Record<string, string> = {
  pending: "yellow",
  confirmed: "cyan",
  preparing: "orange",
  ready: "green",
  completed: "gray",
  cancelled: "red",
};

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, error } = useOrder(id!);

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !order) {
    return (
      <Container py="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title={t("orderDetail.error")}
          mb="md"
        >
          {t("orderDetail.errorDescription")}
        </Alert>
        <Anchor component={Link} to="/orders">
          <IconArrowLeft size={16} style={{ marginRight: 4 }} />
          {t("orderDetail.backToOrders")}
        </Anchor>
      </Container>
    );
  }

  return (
    <Container py="md">
      <Anchor component={Link} to="/orders" mb="md" display="inline-block">
        <IconArrowLeft size={16} style={{ marginRight: 4 }} />
        {t("orderDetail.backToOrders")}
      </Anchor>

      <Group justify="space-between" align="start" mb="lg">
        <Title order={1}>
          {t("orderDetail.title")}
          {order.id}
        </Title>
        <Badge color={statusColors[order.status]} size="xl">
          {t(`orders.status.${order.status}`)}
        </Badge>
      </Group>

      <Stack gap="md">
        <Paper withBorder p="md">
          <Title order={3} mb="md">
            {t("orderDetail.orderInfo")}
          </Title>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text c="dimmed">{t("orderDetail.orderDate")}:</Text>
              <Text>{new Date(order.created_on).toLocaleString()}</Text>
            </Group>
            <Group justify="space-between">
              <Text c="dimmed">{t("orderDetail.paymentMethod")}:</Text>
              <Text>
                {order.payment_method === "cash"
                  ? t("orderDetail.cashOnPickup")
                  : t("orderDetail.card")}
              </Text>
            </Group>
            {order.notes && (
              <Group justify="space-between" align="start">
                <Text c="dimmed">{t("orderDetail.specialInstructions")}:</Text>
                <Text style={{ flex: 1, textAlign: "right" }}>
                  {order.notes}
                </Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Title order={3} mb="md">
            {t("orderDetail.orderItems")}
          </Title>
          <Stack gap="sm">
            {order.items.map((item) => (
              <Group key={item.id} justify="space-between">
                <div>
                  <Text>
                    <Text component="span" fw={600}>
                      {item.quantity}×
                    </Text>{" "}
                    Item #{item.menu_item_id}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatPrice(item.unit_price)} {t("orderDetail.each")}
                  </Text>
                </div>
                <Text fw={600}>{formatPrice(item.subtotal)}</Text>
              </Group>
            ))}
            <Divider my="xs" />
            <Group justify="space-between">
              <Text fw={700} size="lg">
                {t("orderDetail.total")}
              </Text>
              <Text fw={700} size="lg">
                {formatPrice(order.total_amount)}
              </Text>
            </Group>
          </Stack>
        </Paper>

        {order.status === "ready" && (
          <Alert
            icon={<IconCircleCheck size={20} />}
            color="green"
            title={t("orderDetail.readyMessage.title")}
          >
            {t("orderDetail.readyMessage.description")}
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
